import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '@core/store';

import { DEFAULT_LIST_FILTERS } from '../constants';
import {
  AddToMode,
  FormDetailTab,
  FormFieldData,
  FormSetupData,
  FormStatus,
  FormWizardData,
  PublishedFormSnapshot,
  TemplateId
} from '../types';

export type FormDuplicateAnalyticsContext = {
  sourceFormId: string;
  sourceFormStatus: FormStatus;
  sourcePaidCount: number;
};

export type WizardFormDataUpdate = {
  setup?: Partial<FormSetupData>;
  fields?: FormFieldData[];
  templateId?: TemplateId | null;
};

export interface FormsState {
  wizardStep: 1 | 2 | 3;
  wizardMode: 'create' | 'edit';
  wizardFormData: Partial<FormWizardData>;
  originalWizardFormData: Partial<FormWizardData> | null;
  wizardSessionStartedAt: number | null;
  wizardBackCount: number;
  wizardStepEnteredAt: number | null;
  wizardStepValidationErrorsShown: number;
  formDuplicateAnalytics: FormDuplicateAnalyticsContext | null;
  originalFields: FormFieldData[];
  selectedTemplate: TemplateId | null;
  publishedFormId: string | null;
  publishedVersion: number | null;
  publishedFormSnapshot: PublishedFormSnapshot | null;
  activeStatusTab: FormStatus | 'all';
  listFilters: { addTo: AddToMode[] };
  activeDetailTab: FormDetailTab;
}

const initialWizardFormData: Partial<FormWizardData> = {
  setup: {
    name: '',
    description: '',
    addToMode: 'group',
    startCollectionFrom: 'next_month'
  },
  fields: [],
  templateId: null
};

const initialState: FormsState = {
  wizardStep: 1,
  wizardMode: 'create',
  wizardFormData: initialWizardFormData,
  originalWizardFormData: null,
  wizardSessionStartedAt: null,
  wizardBackCount: 0,
  wizardStepEnteredAt: null,
  wizardStepValidationErrorsShown: 0,
  formDuplicateAnalytics: null,
  originalFields: [],
  selectedTemplate: null,
  publishedFormId: null,
  publishedVersion: null,
  publishedFormSnapshot: null,
  activeStatusTab: 'all',
  listFilters: DEFAULT_LIST_FILTERS,
  activeDetailTab: 'dashboard'
};

export const formsSlice = createSlice({
  name: 'forms',
  initialState,
  reducers: {
    setWizardStep: (state, action: PayloadAction<1 | 2 | 3>) => {
      state.wizardStep = action.payload;
    },
    setWizardMode: (state, action: PayloadAction<'create' | 'edit'>) => {
      state.wizardMode = action.payload;
    },
    startWizardSession: (state) => {
      if (state.wizardSessionStartedAt === null) {
        state.wizardSessionStartedAt = Date.now();
      }

      if (state.wizardStepEnteredAt === null) {
        state.wizardStepEnteredAt = Date.now();
        state.wizardStepValidationErrorsShown = 0;
      }
    },
    incrementWizardBackCount: (state) => {
      state.wizardBackCount += 1;
    },
    incrementWizardValidationErrorsShown: (state) => {
      state.wizardStepValidationErrorsShown += 1;
    },
    completeWizardStepTransition: (state, action: PayloadAction<1 | 2 | 3>) => {
      state.wizardStep = action.payload;
      state.wizardStepEnteredAt = Date.now();
      state.wizardStepValidationErrorsShown = 0;
    },
    setFormDuplicateAnalytics: (
      state,
      action: PayloadAction<FormDuplicateAnalyticsContext>
    ) => {
      state.formDuplicateAnalytics = action.payload;
    },
    clearFormDuplicateAnalytics: (state) => {
      state.formDuplicateAnalytics = null;
    },
    setOriginalFields: (state, action: PayloadAction<FormFieldData[]>) => {
      state.originalFields = action.payload;
    },
    setOriginalWizardFormData: (
      state,
      action: PayloadAction<Partial<FormWizardData>>
    ) => {
      state.originalWizardFormData = action.payload;
    },
    updateWizardFormData: (
      state,
      action: PayloadAction<WizardFormDataUpdate>
    ) => {
      const { setup, fields, templateId } = action.payload;
      state.wizardFormData = {
        ...state.wizardFormData,
        ...action.payload,
        setup: setup
          ? ({
              ...state.wizardFormData.setup,
              ...setup
            } as FormSetupData)
          : state.wizardFormData.setup,
        fields: fields ?? state.wizardFormData.fields,
        templateId:
          templateId !== undefined
            ? templateId
            : state.wizardFormData.templateId
      };
    },
    setSelectedTemplate: (state, action: PayloadAction<TemplateId | null>) => {
      state.selectedTemplate = action.payload;
    },
    setPublishedForm: (state, action: PayloadAction<PublishedFormSnapshot>) => {
      state.publishedFormId = action.payload.formId;
      state.publishedVersion = action.payload.version;
      state.publishedFormSnapshot = action.payload;
    },
    resetWizard: (state) => {
      state.wizardStep = initialState.wizardStep;
      state.wizardMode = initialState.wizardMode;
      state.wizardFormData = initialWizardFormData;
      state.originalWizardFormData = initialState.originalWizardFormData;
      state.wizardSessionStartedAt = initialState.wizardSessionStartedAt;
      state.wizardBackCount = initialState.wizardBackCount;
      state.wizardStepEnteredAt = initialState.wizardStepEnteredAt;
      state.wizardStepValidationErrorsShown =
        initialState.wizardStepValidationErrorsShown;
      state.formDuplicateAnalytics = initialState.formDuplicateAnalytics;
      state.originalFields = initialState.originalFields;
      state.selectedTemplate = initialState.selectedTemplate;
    },
    setActiveStatusTab: (state, action: PayloadAction<FormStatus | 'all'>) => {
      state.activeStatusTab = action.payload;
    },
    setListFilters: (
      state,
      action: PayloadAction<Partial<{ addTo: AddToMode[] }>>
    ) => {
      state.listFilters = {
        ...state.listFilters,
        ...action.payload
      };
    },
    setActiveDetailTab: (state, action: PayloadAction<FormDetailTab>) => {
      state.activeDetailTab = action.payload;
    }
  }
});

export const {
  setWizardStep,
  setWizardMode,
  startWizardSession,
  incrementWizardBackCount,
  incrementWizardValidationErrorsShown,
  completeWizardStepTransition,
  setFormDuplicateAnalytics,
  clearFormDuplicateAnalytics,
  setOriginalFields,
  setOriginalWizardFormData,
  updateWizardFormData,
  setSelectedTemplate,
  setPublishedForm,
  resetWizard,
  setActiveStatusTab,
  setListFilters,
  setActiveDetailTab
} = formsSlice.actions;

export const selectFormsState = (state: RootState) => state.forms;
export const selectWizardStep = (state: RootState) => state.forms.wizardStep;
export const selectWizardMode = (state: RootState) => state.forms.wizardMode;
export const selectWizardFormData = (state: RootState) =>
  state.forms.wizardFormData;
export const selectOriginalFields = (state: RootState) =>
  state.forms.originalFields;
export const selectOriginalWizardFormData = (state: RootState) =>
  state.forms.originalWizardFormData;
export const selectWizardSessionStartedAt = (state: RootState) =>
  state.forms.wizardSessionStartedAt;
export const selectWizardBackCount = (state: RootState) =>
  state.forms.wizardBackCount;
export const selectWizardStepEnteredAt = (state: RootState) =>
  state.forms.wizardStepEnteredAt;
export const selectWizardStepValidationErrorsShown = (state: RootState) =>
  state.forms.wizardStepValidationErrorsShown;
export const selectFormDuplicateAnalytics = (state: RootState) =>
  state.forms.formDuplicateAnalytics;
export const selectSelectedTemplate = (state: RootState) =>
  state.forms.selectedTemplate;
export const selectPublishedFormId = (state: RootState) =>
  state.forms.publishedFormId;
export const selectPublishedVersion = (state: RootState) =>
  state.forms.publishedVersion;
export const selectPublishedFormSnapshot = (state: RootState) =>
  state.forms.publishedFormSnapshot;
export const selectActiveStatusTab = (state: RootState) =>
  state.forms.activeStatusTab;
export const selectListFilters = (state: RootState) => state.forms.listFilters;
export const selectActiveDetailTab = (state: RootState) =>
  state.forms.activeDetailTab;

export default formsSlice.reducer;
